import { FC, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import { SimpleBinaryExpression, Operator, FieldMetadata, OperatorInfo } from '../../models/waterfall';
import styles from './SimpleExpressionForm.module.css';

interface SimpleExpressionFormProps {
  onSubmit?: (expression: SimpleBinaryExpression) => void;
  onCancel?: () => void;
  initialValue?: SimpleBinaryExpression;
  onChange?: (expression: SimpleBinaryExpression | null) => void;
  showDelete?: boolean;
  onAddGroup?: () => void;
  showAddGroup?: boolean;
  fieldMetadata?: FieldMetadata[];
  operators?: OperatorInfo[];
}

interface FormValues {
  left: string;
  operator: string;
  right: string;
}

const validationSchema = yup.object().shape({
  left: yup.string().required('Field is required'),
  operator: yup.string().required('Operator is required'),
  right: yup.string().trim().required('Value is required'),
});

export const SimpleExpressionForm: FC<SimpleExpressionFormProps> = ({
  onSubmit: _onSubmit,
  onCancel,
  initialValue,
  onChange,
  showDelete = true,
  onAddGroup,
  showAddGroup = false,
  fieldMetadata,
  operators,
}) => {
  const defaultOperator = operators && operators.length > 0 ? operators[0].operator : 'Equal';
  
  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      left: initialValue?.left || '',
      operator: initialValue?.operator || defaultOperator,
      right: initialValue?.right?.[0] || '',
    },
  });

  const lastInitialValueRef = useRef<string>('');
  useEffect(() => {
    const initialValueKey = JSON.stringify(initialValue);
    if (lastInitialValueRef.current !== initialValueKey) {
      const newValues = {
        left: initialValue?.left || '',
        operator: initialValue?.operator || defaultOperator,
        right: initialValue?.right?.[0] || '',
      };
      console.log('[SimpleExpressionForm] Resetting form with new initialValue:', newValues);
      reset(newValues);
      lastInitialValueRef.current = initialValueKey;
    }
  }, [initialValue, reset, defaultOperator]);

  const formValues = watch();
  
  console.log('[SimpleExpressionForm] formValues:', formValues);
  console.log('[SimpleExpressionForm] fieldMetadata:', fieldMetadata);
  
  const fieldOptions = useMemo(() => {
    if (!fieldMetadata || fieldMetadata.length === 0) {
      console.log('[SimpleExpressionForm] No fieldMetadata, returning empty options');
      return [];
    }
    const options = fieldMetadata.map((field) => ({
      label: field.fieldName || '',
      value: field.fieldName || '',
    }));
    console.log('[SimpleExpressionForm] Generated fieldOptions:', options);
    return options;
  }, [fieldMetadata]);

  useEffect(() => {
    console.log('[SimpleExpressionForm] useEffect triggered - formValues changed:', {
      left: formValues.left,
      operator: formValues.operator,
      right: formValues.right,
    });
    
    if (onChange) {
      if (formValues.left && formValues.operator && formValues.right?.trim()) {
        const expression = {
          left: formValues.left,
          operator: formValues.operator as Operator,
          right: [formValues.right.trim()],
        } as SimpleBinaryExpression;
        console.log('[SimpleExpressionForm] Calling onChange with expression:', expression);
        onChange(expression);
      } else {
        console.log('[SimpleExpressionForm] Calling onChange with null (incomplete form)');
        onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.left, formValues.operator, formValues.right]);

  const operatorOptions = useMemo(() => {
    if (!operators || operators.length === 0) {
      console.log('[SimpleExpressionForm] No operators, returning empty options');
      return [];
    }
    const options = operators.map((op) => ({
      label: op.name || op.operator,
      value: op.operator,
    }));
    console.log('[SimpleExpressionForm] Generated operatorOptions:', options);
    return options;
  }, [operators]);

  return (
    <div className={`p-fluid p-input-filled flex gap-2 text-sm w-full ${styles.form}`}>
      <div className="min-w-42 flex-1">
        <FormDropdown
          control={control}
          errors={errors}
          field="left"
          label=""
          options={fieldOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Select field"
          showFilter={true}
          className="mb-0"
        />
      </div>

      <div className="w-42">
        <FormDropdown
          control={control}
          errors={errors}
          field="operator"
          label=""
          options={operatorOptions}
          optionLabel="label"
          optionValue="value"
          showFilter={true}
          className="mb-0"
        />
      </div>

      <div className="min-w-42 flex-1">
        <FormInputText control={control} errors={errors} field="right" label="" placeholder="Value" className="mb-0" />
      </div>

      {onAddGroup && showAddGroup && (
        <Button
          type="button"
          icon="pi pi-plus"
          className="p-button-sm p-button-text"
          onClick={onAddGroup}
          tooltip="Create nested group"
        />
      )}

      {onCancel && showDelete && (
        <Button type="button" icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={onCancel} />
      )}
    </div>
  );
};
